// src/hooks/usePriceQuotesData.js

import { useState, useEffect, useMemo } from 'react';
import { message } from 'antd';
import { paginationEstimates } from '~/services/hooks/estimates';
import { paginationSubVendors } from '~/services/hooks/subVendors';
import { paginationLaborTypes } from '~/services/hooks/laborTypes';
import { findAllEstimateSubVendors, updateEstimateSubVendor } from '~/services/hooks/estimateSubVendors';

/**
 * Hook to manage the state and business logic of the PriceQuotes component
 * @param {number|string} projectId
 */
export function usePriceQuotesData(projectId) {
  const [loading, setLoading] = useState(false);
  const [estimateData, setEstimateData] = useState(null);
  const [allSubVendors, setAllSubVendors] = useState([]);
  const [allLaborTypes, setAllLaborTypes] = useState([]);
  const [winners, setWinners] = useState([]);
  const [showType, setShowType] = useState(1); // null?
  const [selectedEstimateSubVendors, setSelectedEstimateSubVendors] = useState([]);
  const [completeEstimate, setCompleteEstimate] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    const data = await paginationEstimates({
      page: 0,
      maxSize: true,
      filters: [{ columnJoin: 'project', field: 'id', value: projectId, restriction: 'EQUAL' }],
    });
    if (data.numberOfElements === 0) {
      message.error('Not found');
    } else {
      setEstimateData(data.content[0]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    paginationSubVendors({
      page: 0,
      maxSize: true,
      filters: [{ field: 'type', value: 'SUBCONTRACTOR', restriction: 'EQUAL' }],
    }).then(data => {
      setAllSubVendors(data.content);
    });
    paginationLaborTypes({ page: 0, sortBy: 'name', maxSize: true, filters: [] }).then(data => {
      setAllLaborTypes(data.content);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    findAllEstimateSubVendors([
      {
        columnJoin: 'estimateItem',
        columnSecondJoin: 'estimate',
        field: 'id',
        value: estimateData?.id,
        restriction: 'EQUAL',
      },
    ]).then(data => {
      setSelectedEstimateSubVendors(data);
    });
  }, [estimateData]);

  useEffect(() => {
    // set winners with selectedEstimateSubVendors that have winner = true
    const newWinners = [];
    const winnersItems = selectedEstimateSubVendors.filter(estimateSubvendor => estimateSubvendor.winner === true);
    winnersItems.forEach(item => {
      const winner = {};
      winner.subVendorId = item.subVendor.id;
      winner.laborTypeId = item.estimateItem.laborType.id;
      // insert winner in newWinners if not exists
      if (
        !newWinners.find(
          winnerItem => winnerItem.subVendorId === winner.subVendorId && winnerItem.laborTypeId === winner.laborTypeId
        )
      ) {
        newWinners.push(winner);
      }
    });
    if (newWinners.length > 0) {
      setWinners(newWinners);
    }
  }, [selectedEstimateSubVendors]);

  // mount a list of selectedEstimateSubvendors with subVendor and estimateItem data
  const mountEstimateSubvendors = () => {
    const complete = [];
    selectedEstimateSubVendors.forEach(element => {
      element.subVendor = allSubVendors.find(subVendor => subVendor.id === element.subvendor_id); // get data from subvendors by subvendor_id
      element.estimateItem = completeEstimate.items.find(estimateItem => estimateItem.id === element.estimate_item_id); // get data from estimate_items by estimate_item_id
      complete.push(element);
    });
    setSelectedEstimateSubVendors(complete);
  };

  // mount a list of subvendors with their items
  // const subVendors = useMemo(
  //   () => allSubVendors.filter(v => selectedEstimateSubVendors.some(s => s.subVendor.id === v.id)),
  //   [allSubVendors, selectedEstimateSubVendors]
  // );

  // mount a list of labor types used in selectedEstimateSubVendors
  const laborTypes = useMemo(
    () => allLaborTypes.filter(lt => selectedEstimateSubVendors.some(s => s.estimateItem.laborType.id === lt.id)),
    [selectedEstimateSubVendors, allLaborTypes]
  );

  // mount a list subvendors with their items
  const mountCompleteEstimate = () => {
    const complete = [];
    const laborType = laborTypes.find(laborType => laborType.id === showType);
    if (allSubVendors.length === 0 || !laborType) return;
    allSubVendors.forEach(subVendor => {
      const items = [];
      selectedEstimateSubVendors
        .filter(estimateSubvendor => estimateSubvendor.subVendor?.id === subVendor.id)
        .filter(estimateSubvendor => estimateSubvendor.estimateItem.laborType?.id === laborType.id)
        .forEach(estimateSubvendor => {
          const item = {
            id: estimateSubvendor.estimateItem.id,
            description: estimateSubvendor.estimateItem.estimateDescription.description,
            unit: estimateSubvendor.estimateItem.estimateDescription.unit,

            ownQuantity: estimateSubvendor.estimateItem.quantity,
            ownMaterialValue: estimateSubvendor.estimateItem.materialValue,
            ownLaborValue: estimateSubvendor.estimateItem.laborValue,

            quantity: estimateSubvendor.quantity,
            materialValue: estimateSubvendor.materialValue,
            laborValue: estimateSubvendor.laborValue,

            projectId: projectId,
            area: estimateSubvendor.estimateItem.area,
            type: estimateSubvendor.estimateItem.type,
            laborType: estimateSubvendor.estimateItem.laborType,
            dueDate: estimateSubvendor.dueDate,
            note: estimateSubvendor.note,
            winner: estimateSubvendor.winner,
          };
          items.push(item);
        });
      if (items.length > 0) {
        complete.push({ subVendor, items });
      }
    });
    setCompleteEstimate(complete);
  };

  useEffect(() => {
    if (!estimateData || allSubVendors.length === 0) return;
    mountEstimateSubvendors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimateData, allSubVendors]);

  useEffect(() => {
    if (allSubVendors.length > 0 && laborTypes.length > 0) mountCompleteEstimate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allSubVendors, laborTypes, showType]);

  const handleWinner = (idLaborType, idSubvendor) => {
    let findWinner = false;
    const newWinners = winners.filter(winner => winner.laborTypeId !== idLaborType);
    if (!winners.find(winner => winner.laborTypeId === idLaborType && winner.subVendorId === idSubvendor)) {
      const winner = { laborTypeId: idLaborType, subVendorId: idSubvendor };
      findWinner = true;
      newWinners.push(winner);
    }
    setWinners(newWinners);

    const newEstimateSubvendors = selectedEstimateSubVendors.map(estimate => {
      if (estimate.subVendor.id === idSubvendor && estimate.estimateItem.laborType.id === idLaborType) {
        estimate.winner = findWinner;
      }
      if (estimate.subVendor.id !== idSubvendor && estimate.estimateItem.laborType.id === idLaborType) {
        estimate.winner = false;
      }
      return estimate;
    });
    setSelectedEstimateSubVendors(newEstimateSubvendors);
  };

  const handleSaveWinners = () => {
    const subVendorsWinners = selectedEstimateSubVendors.filter(estimateSubvendor => estimateSubvendor.winner === true);
    subVendorsWinners.forEach(async subVendorWinner => {
      await updateEstimateSubVendor(subVendorWinner);
    });
  };

  return {
    loading,
    laborTypes,
    completeEstimate,
    winners,
    showType,
    setShowType,
    handleWinner,
    handleSaveWinners,
  };
}
